import {
  gql,
  type ApolloClient,
  type OnDataOptions,
  type Reference,
} from '@apollo/client';
import { useCallback, type FunctionComponent } from 'react';

import {
  dangerouslyGetFeatureGateSync,
  useFeatureGate,
} from '@trello/feature-gate-client';
import type { TrelloMirrorCardSubscriptionCardFragment } from '@trello/graphql';
// eslint-disable-next-line no-restricted-imports
import type {
  CustomField,
  CustomField_Type,
  TrelloChecklistUpdated,
} from '@trello/graphql/generated';
import { idCache } from '@trello/id-cache';
import type { Board, Card, Label } from '@trello/model-types';
import { convertToUGCString, type UGCString } from '@trello/privacy';

import type { MirrorCardFragment } from './MirrorCardFragment.generated';
import { MirrorCardFragmentDoc } from './MirrorCardFragment.generated';
import {
  MirrorCardSourceBoardCustomFieldsFragmentDoc,
  type MirrorCardSourceBoardCustomFieldsFragment,
} from './MirrorCardSourceBoardCustomFieldsFragment.generated';
import {
  MirrorCardSourceBoardInfoFragmentDoc,
  type MirrorCardSourceBoardInfoFragment,
} from './MirrorCardSourceBoardInfoFragment.generated';
import {
  useTrelloMirrorCardsSplitSubscription,
  type TrelloMirrorCardsSplitSubscription,
} from './TrelloMirrorCardsSplitSubscription.generated';
import {
  useTrelloMirrorCardsSubscription,
  type TrelloMirrorCardsSubscription,
} from './TrelloMirrorCardsSubscription.generated';

type Sticker = NonNullable<MirrorCardFragment>['stickers'][number];
type Checklist = NonNullable<MirrorCardFragment>['checklists'][number];
type Badges = NonNullable<MirrorCardFragment>['badges'];
type Attachment = NonNullable<MirrorCardFragment>['attachments'][number];

interface CustomFieldItemValue {
  __typename: 'CustomFieldItem_Value';
  text: string | null;
  number: string | null;
  date: string | null;
  checked: string | null;
}

interface CustomFieldItem {
  __typename: 'CustomFieldItem';
  id: string;
  idCustomField: string;
  idValue?: string | null;
  idModel: string;
  modelType: 'card';
  value?: CustomFieldItemValue | null | undefined;
}

export const getSubscriptionData = (
  data: OnDataOptions<
    TrelloMirrorCardsSplitSubscription | TrelloMirrorCardsSubscription
  >,
) => {
  const trelloData = data?.data?.data?.trello;
  if (!trelloData) {
    return undefined;
  }

  if ('onBoardUpdated' in trelloData) {
    return trelloData.onBoardUpdated;
  }

  if ('onBoardCardSetUpdated' in trelloData) {
    return trelloData.onBoardCardSetUpdated;
  }

  return undefined;
};

const updateList = (
  data: OnDataOptions<
    TrelloMirrorCardsSplitSubscription | TrelloMirrorCardsSubscription
  >,
) => {
  const subscriptionData = getSubscriptionData(data);
  if (!subscriptionData) {
    return;
  }

  const isTypePolicyCacheSyncingEnabled = dangerouslyGetFeatureGateSync(
    'goo_cache_sync_in_separate_type_policies',
  );

  const list = subscriptionData.lists?.edges?.[0]?.node;

  const client = data.client;
  const listName = list?.name;
  if (!isTypePolicyCacheSyncingEnabled && listName) {
    const listId = list?.objectId;
    if (listId) {
      client.cache.modify({
        id: client.cache.identify({
          id: listId,
          __typename: 'List',
        }),
        fields: {
          name: () => listName,
        },
        optimistic: true,
      });
    }
  }
};

const updateBadges: (
  existingCard: MirrorCardFragment | null,
  updatedCard: TrelloMirrorCardSubscriptionCardFragment,
) => Badges | undefined = (
  existingCard: MirrorCardFragment | null,
  updatedCard: TrelloMirrorCardSubscriptionCardFragment,
) => {
  const existingBadges = existingCard?.badges;
  const updatedBadges = {
    __typename: 'Card_Badges' as const,
    attachments:
      updatedCard.badges?.attachments ?? existingBadges?.attachments ?? 0,
    checkItems:
      updatedCard.badges?.checkItems ?? existingBadges?.checkItems ?? 0,
    checkItemsChecked:
      updatedCard.badges?.checkItemsChecked ??
      existingBadges?.checkItemsChecked ??
      0,
    checkItemsEarliestDue:
      updatedCard.badges?.checkItemsEarliestDue ??
      existingBadges?.checkItemsEarliestDue ??
      null,
    comments: updatedCard.badges?.comments ?? existingBadges?.comments ?? 0,
    due:
      updatedCard.badges?.due === null
        ? null
        : (updatedCard.badges?.due?.at ?? existingBadges?.due ?? null),
    dueComplete:
      updatedCard.badges?.due?.complete ?? existingBadges?.dueComplete ?? false,
    description:
      updatedCard.badges?.description ?? existingBadges?.description ?? false,
    lastUpdatedByAi: existingBadges?.lastUpdatedByAi ?? false,
    location: updatedCard.badges?.location ?? existingBadges?.location ?? false,
    start: updatedCard.badges?.startedAt ?? existingBadges?.start ?? null,
    votes: updatedCard.badges?.votes ?? existingBadges?.votes ?? 0,
    attachmentsByType: existingBadges?.attachmentsByType ?? {
      __typename: 'Card_Badges_AttachmentsByType',
      trello: {
        __typename: 'Card_Badges_AttachmentsByType_Trello',
        board: 0,
        card: 0,
      },
    },
    externalSource: existingBadges?.externalSource ?? null,
    subscribed: existingBadges?.subscribed ?? false,
    viewingMemberVoted: existingBadges?.viewingMemberVoted ?? false,
    maliciousAttachments: existingBadges?.maliciousAttachments ?? 0,
  };
  const diff = Object.keys(updatedBadges).filter(
    (key) =>
      key !== '__typename' &&
      updatedBadges[key as keyof typeof updatedBadges] !==
        existingBadges?.[key as keyof typeof existingBadges],
  );
  if (diff.length > 0) {
    return updatedBadges;
  }
};

const updateCustomFieldItems = (
  existingCard: MirrorCardFragment | null,
  updatedCard: TrelloMirrorCardSubscriptionCardFragment,
  client: ApolloClient<object>,
) => {
  const existingCustomFieldItems = [...(existingCard?.customFieldItems ?? [])];
  const updatedCustomFieldItems: Reference[] = [];
  updatedCard.customFieldItems?.edges?.map((customFieldItem) => {
    const existingCustomFieldItem = existingCustomFieldItems?.find(
      (item) => item.id === customFieldItem.node.objectId,
    );
    if (existingCustomFieldItem) {
      client.cache.modify<CustomFieldItem>({
        id: client.cache.identify({
          id: customFieldItem.node.objectId,
          __typename: 'CustomFieldItem',
        }),
        fields: {
          idValue: (prev) => customFieldItem.node.value?.id ?? prev,
          value: () => {
            return {
              __typename: 'CustomFieldItem_Value',
              text: customFieldItem.node.value?.text ?? null,
              number: customFieldItem.node.value?.number?.toString() ?? null,
              date: customFieldItem.node.value?.date ?? null,
              checked: customFieldItem.node.value?.checked?.toString() ?? null,
            };
          },
        },
      });
    } else {
      const newCustomFieldItem = client.cache.writeFragment({
        data: {
          __typename: 'CustomFieldItem',
          id: customFieldItem.node.objectId,
          idCustomField: customFieldItem.node.customField?.objectId || '',
          idModel: updatedCard.objectId || '',
          modelType: 'card',
          idValue: customFieldItem.node.value?.id,
          value: {
            __typename: 'CustomFieldItem_Value',
            text: customFieldItem.node.value?.text ?? null,
            number: customFieldItem.node.value?.number ?? null,
            date: customFieldItem.node.value?.date ?? null,
            checked: customFieldItem.node.value?.checked?.toString() ?? null,
          },
        },
        fragment: gql`
          fragment NewCustomFieldItem on CustomFieldItem {
            id
            idCustomField
            idModel
            modelType
            idValue
            value {
              text
              number
              date
              checked
            }
          }
        `,
      });
      if (newCustomFieldItem) {
        updatedCustomFieldItems.push(newCustomFieldItem);
      }
    }
  });
  return updatedCustomFieldItems;
};

const updateBoard = (
  boardIdOrShortLink: string,
  data: OnDataOptions<
    TrelloMirrorCardsSplitSubscription | TrelloMirrorCardsSubscription
  >,
) => {
  const client = data.client;
  const isTypePolicyCacheSyncingEnabled = dangerouslyGetFeatureGateSync(
    'goo_cache_sync_in_separate_type_policies',
  );

  const subscriptionData = getSubscriptionData(data);
  if (!subscriptionData) {
    return;
  }

  const boardId =
    boardIdOrShortLink.length === 8
      ? (idCache.getBoardId(boardIdOrShortLink) ?? '')
      : boardIdOrShortLink;

  const existingBoard = client.readFragment<MirrorCardSourceBoardInfoFragment>({
    id: client.cache.identify({
      id: boardId,
      __typename: 'Board',
    }),
    fragment: MirrorCardSourceBoardInfoFragmentDoc,
  });
  if (subscriptionData.name && existingBoard?.name !== subscriptionData.name) {
    client.cache.modify<Board>({
      id: client.cache.identify({
        id: boardId,
        __typename: 'Board',
      }),
      fields: {
        name: () => subscriptionData.name || '',
      },
    });
  }
  if (subscriptionData.customFields?.edges) {
    const existingCustomFields =
      client.readFragment<MirrorCardSourceBoardCustomFieldsFragment>({
        id: client.cache.identify({
          id: boardId,
          __typename: 'Board',
        }),
        fragment: MirrorCardSourceBoardCustomFieldsFragmentDoc,
      });
    subscriptionData.customFields.edges.map((customField) => {
      if (
        existingCustomFields?.customFields?.some(
          (field) => field.id === customField.node.objectId,
        )
      ) {
        const existingField = client.cache.identify({
          id: customField.node.objectId,
          __typename: 'CustomField',
        });
        if (existingField) {
          client.cache.modify<CustomField>({
            id: existingField,
            fields: {
              name: (prev) => customField.node.name ?? prev,
              options: (prev) =>
                customField.node.options?.map((option) => ({
                  __typename: 'CustomField_Option' as const,
                  id: option.objectId,
                  idCustomField: customField.node.objectId,
                  pos: option.position ?? 0,
                  value: {
                    __typename: 'CustomField_Option_Value' as const,
                    text: option.value?.text ?? '',
                  },
                  color: option.color ?? '',
                })) ?? prev,
            },
          });
        }
      } else {
        client.cache.modify<Board>({
          id: client.cache.identify({
            id: boardId,
            __typename: 'Board',
          }),
          fields: {
            customFields: (prev) => {
              const newCustomFieldRef = client.cache.writeFragment({
                data: {
                  __typename: 'CustomField',
                  id: customField.node.objectId,
                  name: customField.node.name ?? '',
                  type: customField.node.type as CustomField_Type,
                  options:
                    customField.node.options?.map((option) => ({
                      __typename: 'CustomField_Option',
                      id: option.objectId,
                      idCustomField: customField.node.objectId,
                      value: {
                        text: option.value?.text ?? '',
                      },
                      color: option.color ?? '',
                      pos: option.position ?? 0,
                    })) ?? [],
                  pos: customField.node.position ?? 0,
                  fieldGroup: '',
                  modelType: 'board',
                  isSuggestedField: false,
                  idModel: boardId,
                  display: {
                    __typename: 'CustomField_Display',
                    cardFront: customField.node.display?.cardFront ?? false,
                  },
                },
                fragment: gql`
                  fragment NewCustomField on CustomField {
                    id
                    name
                    type
                    options {
                      id
                      idCustomField
                      value {
                        text
                      }
                      color
                      pos
                    }
                    pos
                    fieldGroup
                    modelType
                    isSuggestedField
                    idModel
                    display {
                      cardFront
                    }
                  }
                `,
              });
              if (!newCustomFieldRef) {
                return prev;
              }
              return [...prev, newCustomFieldRef];
            },
          },
        });
      }
    });
  }
  if (
    !isTypePolicyCacheSyncingEnabled &&
    subscriptionData.onCustomFieldDeleted
  ) {
    subscriptionData.onCustomFieldDeleted.map((customField) => {
      const existingField = client.cache.identify({
        id: customField.objectId,
        __typename: 'CustomField',
      });
      if (existingField) {
        client.cache.evict({ id: existingField });
      }
      client.cache.modify<Board>({
        id: client.cache.identify({
          id: boardId,
          __typename: 'Board',
        }),
        fields: {
          customFields: (prev, { readField }) =>
            prev.filter(
              (field) => readField('id', field) !== customField.objectId,
            ),
        },
      });
    });
  }
};

/**
 * Updates an existing checklist directly in the cache
 */
const updateChecklist = (
  client: ApolloClient<object>,
  checklist: TrelloChecklistUpdated,
) => {
  const checklistFields: {
    [key: string]: () => UGCString | number | string;
  } = {};

  // Since some fields may be set to 'null' incorrectly and we don't have delta processing
  // code here, check that each field is non-null before updating it

  if (typeof checklist.position === 'number') {
    const newPosition = checklist.position;
    checklistFields.pos = () => newPosition;
  }

  if (typeof checklist.name === 'string') {
    const newName = checklist.name;
    checklistFields.name = () => convertToUGCString(newName);
  }

  // TODO: add handling for checkItems

  const checklistCacheId = client.cache.identify({
    id: checklist.objectId,
    __typename: 'Checklist',
  });
  client.cache.modify<Checklist>({
    id: checklistCacheId,
    fields: checklistFields,
    optimistic: true,
  });
};

const updateCards = (
  data: OnDataOptions<
    TrelloMirrorCardsSplitSubscription | TrelloMirrorCardsSubscription
  >,
  cardRealIds: string[],
  boardId: string,
) => {
  const client = data.client;
  const isTypePolicyCacheSyncingEnabled = dangerouslyGetFeatureGateSync(
    'goo_cache_sync_in_separate_type_policies',
  );

  const subscriptionData = getSubscriptionData(data);
  if (!subscriptionData) {
    return;
  }

  const updatedCards = subscriptionData.lists?.edges?.[0]?.node?.cards?.edges
    ?.map((c) => c.node)
    ?.filter((c) => c.objectId && cardRealIds.includes(c.objectId));
  if (updatedCards) {
    updatedCards.forEach((updatedCard) => {
      const existingCard = client.readFragment<MirrorCardFragment>({
        id: client.cache.identify({
          id: updatedCard.objectId,
          __typename: 'Card',
        }),
        fragment: MirrorCardFragmentDoc,
      });
      const cacheId = client.cache.identify({
        id: updatedCard.objectId,
        __typename: 'Card',
      });

      const fields: {
        [key: string]: () =>
          | Attachment[]
          | Badges
          | Checklist[]
          | CustomFieldItem[]
          | Label[]
          | Sticker[]
          | string[]
          | boolean
          | number
          | string
          | { __typename: string }
          | { __typename: string; latitude: number; longitude: number };
      } = {};

      if (
        !isTypePolicyCacheSyncingEnabled &&
        updatedCard.name !== existingCard?.name
      ) {
        fields.name = () => updatedCard.name || existingCard?.name || '';
      }

      if (
        !isTypePolicyCacheSyncingEnabled &&
        updatedCard.closed !== existingCard?.closed
      ) {
        fields.closed = () =>
          updatedCard.closed || existingCard?.closed || false;
      }

      if (updatedCard.attachments?.edges) {
        fields.attachments = () =>
          updatedCard.attachments?.edges?.map(
            (edge): Attachment => ({
              __typename: 'Attachment' as const,
              id: edge.node.objectId,
              isMalicious: edge.node.isMalicious || false,
              name: convertToUGCString(edge.node.name || ''),
              url: convertToUGCString(edge.node.url || ''),
              date: convertToUGCString(edge.node.date || ''),
              idMember: edge.node.creatorId || '',
              pos: edge.node.position || 0,
              bytes: edge.node.bytes || 0,
              edgeColor: edge.node.edgeColor || '',
              fileName: edge.node.fileName || '',
              isUpload: edge.node.isUpload || false,
              mimeType: edge.node.mimeType || '',
            }),
          ) ??
          existingCard?.attachments ??
          [];
      }

      if (
        !isTypePolicyCacheSyncingEnabled &&
        subscriptionData.lists?.edges?.[0]?.node?.objectId &&
        existingCard?.idList !==
          subscriptionData.lists?.edges?.[0]?.node?.objectId
      ) {
        const listId = subscriptionData.lists?.edges?.[0]?.node?.objectId;
        if (listId) {
          client.cache.modify<Card>({
            id: cacheId,
            fields: {
              list: (existing, { toReference }) => {
                const ref = toReference({ __typename: 'List', id: listId });
                if (!ref) {
                  return existing;
                }
                return ref;
              },
              idList: () => listId,
            },
            optimistic: true,
          });
        }
      }

      if (!isTypePolicyCacheSyncingEnabled && updatedCard.labels?.nodes) {
        let updatedLabels: Label[] = [];
        if (updatedCard.labels.nodes) {
          updatedLabels = updatedCard.labels.nodes.map((label) => ({
            __typename: 'Label',
            id: label.id,
            color: (label.color as Label['color']) ?? '',
            idBoard: existingCard?.board?.id ?? '',
            name: label.name ?? '',
          }));
          fields.labels = () => updatedLabels;
        }
      }
      if (!isTypePolicyCacheSyncingEnabled && updatedCard.badges) {
        const updatedBadges = updateBadges(existingCard, updatedCard);
        if (updatedBadges) {
          fields.badges = () => updatedBadges;
        }
      }
      if (!isTypePolicyCacheSyncingEnabled && updatedCard.stickers?.edges) {
        const updatedStickers: Sticker[] = updatedCard.stickers.edges?.map(
          (sticker) => ({
            __typename: 'Sticker',
            id: sticker.node.objectId,
            image: sticker.node.image || '',
            imageUrl: sticker.node.url || '',
            imageScaled: [
              {
                __typename: 'Sticker_ImageScaled',
                url: sticker.node.url || '',
                height: 140,
                scaled: false,
                width: 140,
              },
            ],
            left: sticker.node.left ?? 0,
            rotate: sticker.node.rotate ?? 0,
            top: sticker.node.top ?? 0,
            zIndex: sticker.node.zIndex ?? 0,
          }),
        );
        fields.stickers = () => updatedStickers;
      }
      if (!isTypePolicyCacheSyncingEnabled && updatedCard.members) {
        const updatedMembers =
          updatedCard.members?.edges?.reduce((acc, member) => {
            acc.push(member.node?.id || '');
            return acc;
          }, [] as string[]) || [];
        fields.idMembers = () => updatedMembers;
      }
      if (
        !isTypePolicyCacheSyncingEnabled &&
        updatedCard.customFieldItems?.edges
      ) {
        const updatedCustomFieldItems = updateCustomFieldItems(
          existingCard,
          updatedCard,
          client,
        );
        client.cache.modify<Card>({
          id: cacheId,
          fields: {
            customFieldItems: (prev) => {
              return [...prev, ...updatedCustomFieldItems];
            },
          },
        });
      }

      const existingChecklistIds = existingCard?.idChecklists ?? [];
      const existingChecklists = existingCard?.checklists ?? [];
      if (updatedCard.checklists?.edges?.[0]) {
        // checklists will only ever contain one checklist, an updated or newly inserted checklist
        const checklist = updatedCard.checklists.edges[0].node;

        const i = existingChecklistIds.findIndex(
          (idChecklist) => idChecklist === checklist.objectId,
        );

        if (i === -1) {
          // Add new checklist
          fields.idChecklists = () => [
            ...existingChecklistIds,
            checklist.objectId,
          ];

          const newChecklist: Checklist = {
            __typename: 'Checklist',
            id: checklist.objectId,
            pos: checklist.position!, // should never be null for new checklists
            name: convertToUGCString(checklist.name ?? ''),
            idBoard: boardId,
            // We already check that objectId is set above so this should never be ''
            idCard: updatedCard.objectId ?? '',
            checkItems: [],
          };
          fields.checklists = () => [...existingChecklists, newChecklist];
        } else {
          // Update existing checklist
          updateChecklist(client, checklist);
        }

        // card.onChecklistDeleted and card.checklists should never both be populated in the same update
      } else if (updatedCard.onChecklistDeleted?.[0]) {
        const deletedChecklistId = updatedCard.onChecklistDeleted[0].objectId;

        // Delete the object from the cache
        const deletedChecklistCacheId = client.cache.identify({
          id: deletedChecklistId,
          __typename: 'Checklist',
        });
        client.cache.evict({ id: deletedChecklistCacheId });

        // Remove dangling reference from card.idChecklists
        const idChecklistsIndex = existingChecklistIds.findIndex(
          (idChecklist) => idChecklist === deletedChecklistId,
        );
        if (idChecklistsIndex > -1) {
          fields.idChecklists = () => [
            ...existingChecklistIds.slice(0, idChecklistsIndex),
            ...existingChecklistIds.slice(idChecklistsIndex + 1),
          ];
        }

        // Remove dangling reference from card.checklists
        const checklistsIndex = existingChecklists.findIndex(
          ({ id }) => id === deletedChecklistId,
        );
        if (checklistsIndex > -1) {
          fields.checklists = () => [
            ...existingChecklists.slice(0, checklistsIndex),
            ...existingChecklists.slice(checklistsIndex + 1),
          ];
        }
      }

      if (updatedCard.location) {
        fields.address = () => updatedCard.location?.address || '';
        fields.locationName = () => updatedCard.location?.name || '';
        if (updatedCard.location?.coordinates) {
          fields.coordinates = () => {
            return {
              __typename: 'Card_Coordinates',
              latitude: updatedCard.location?.coordinates?.latitude || 0,
              longitude: updatedCard.location?.coordinates?.longitude || 0,
            };
          };
        }
      }
      if (!isTypePolicyCacheSyncingEnabled && updatedCard.due) {
        fields.due = () => updatedCard.due?.at || '';
      }
      if (
        !isTypePolicyCacheSyncingEnabled &&
        updatedCard.complete !== undefined
      ) {
        fields.dueComplete = () => updatedCard.complete || false;
      }
      if (updatedCard.position) {
        fields.pos = () => updatedCard.position || 0;
      }
      client.cache.modify<Card>({
        id: cacheId,
        fields,
      });
    });
  }
};

const onDataFn: (
  boardId: string,
  cardRealIds: string[],
) => (data: OnDataOptions<TrelloMirrorCardsSubscription>) => void =
  (boardId, cardRealIds) => (data) => {
    updateList(data);
    updateBoard(boardId, data);
    updateCards(data, cardRealIds, boardId);
  };

export const MirrorCardBoardSubscription: FunctionComponent<{
  boardId: string;
  cardIds: string[];
}> = ({ boardId, cardIds }) => {
  const boardAri = idCache.getBoardAri(boardId) ?? '';
  const cardAris = cardIds.map((cardId) => idCache.getCardAri(cardId) ?? '');
  const cardRealIds = cardIds.map((cardId) => idCache.getCardId(cardId) ?? '');

  const onData = useCallback(
    (data: OnDataOptions<TrelloMirrorCardsSubscription>) =>
      onDataFn(boardId, cardRealIds)(data),
    [cardRealIds, boardId],
  );

  const mirrorCardParmsAreInValid =
    cardAris.length === 0 || !boardAri || cardAris.some((cardAri) => !cardAri);

  const { value: isSplitSubscriptionEnabled } = useFeatureGate(
    'goo_subscribe_split_card_update',
  );

  useTrelloMirrorCardsSplitSubscription({
    variables: { boardId: boardAri, nodeIds: cardAris },
    skip: !isSplitSubscriptionEnabled || mirrorCardParmsAreInValid,
    onData,
  });

  useTrelloMirrorCardsSubscription({
    variables: { boardId: boardAri, nodeIds: cardAris },
    skip: isSplitSubscriptionEnabled || mirrorCardParmsAreInValid,
    onData,
  });

  return null;
};
